"use client";

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  MessageCircle,
  GraduationCap,
  Mic,
  Play,
  Lightbulb,
  PartyPopper,
  TrendingUp,
  Users,
  Clock
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import { cn } from '@/lib/utils';
import { useCommunityStatistics } from '@/hooks/useCommunityStatistics';
import PollSidebarCard from '@/components/poll/PollSidebarCard';

// The "Browse by Category" section is intentionally hidden pending a
// redesign — categories aren't surfaced well in the feed yet and the
// list duplicated the chip strip above. Flip this back to true (or
// remove the gate) once the redesign lands. The categoryConfig array
// below is preserved so the future redesign can reuse it.
const SHOW_BROWSE_BY_CATEGORY = false;

// Hidden until activity is high enough to display meaningfully —
// counts of 0 read worse than absence. Markup and the
// useCommunityStatistics fetch below are preserved so this can flip
// back on once the platform has consistent daily activity.
const SHOW_STATS_CARD = false;

interface PostCategoryNavigationProps {
  activeCategory?: string;
  onCategoryChange?: (category: string) => void;
  showCounts?: boolean;
  className?: string;
}

const categoryConfig = [
  { 
    id: 'general', 
    label: 'General', 
    icon: MessageCircle, 
    color: 'bg-blue-500',
    description: 'General discussions and updates'
  },
  { 
    id: 'workshop', 
    label: 'Workshops', 
    icon: GraduationCap, 
    color: 'bg-purple-500',
    description: 'Workshop announcements and discussions'
  },
  { 
    id: 'audition', 
    label: 'Auditions', 
    icon: Mic, 
    color: 'bg-green-500',
    description: 'Casting calls and audition opportunities'
  },
  { 
    id: 'demo', 
    label: 'Demos', 
    icon: Play, 
    color: 'bg-orange-500',
    description: 'Share your latest voice demos'
  },
  { 
    id: 'tip', 
    label: 'Tips', 
    icon: Lightbulb, 
    color: 'bg-yellow-500',
    description: 'Voice acting tips and techniques'
  },
  { 
    id: 'celebration', 
    label: 'Celebrations', 
    icon: PartyPopper, 
    color: 'bg-pink-500',
    description: 'Celebrate achievements and milestones'
  }
];

export default function PostCategoryNavigation({ 
  activeCategory, 
  onCategoryChange, 
  showCounts = false, // Disabled category counts
  className 
}: PostCategoryNavigationProps) {
  const router = useRouter();

  const handleCategoryClick = (categoryId: string) => {
    if (onCategoryChange) {
      onCategoryChange(categoryId);
    } else {
      // Navigate to category-specific page
      router.push(`/community?category=${categoryId}`);
    }
  };

  const { statistics, loading } = useCommunityStatistics();

  return (
    <div className={cn("space-y-4", className)}>
      {/* Quick Stats — hidden until activity is meaningful (see flag at top). */}
      {SHOW_STATS_CARD && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-surface-2 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-status-online" />
              <span className="text-sm font-medium text-text-primary">Trending</span>
            </div>
            <p className="text-2xl font-bold text-status-online">
              {loading ? '—' : statistics?.trending.topics || 0}
            </p>
            <p className="text-xs text-text-tertiary">topics today</p>
          </div>

          <div className="bg-surface-2 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Users className="h-4 w-4 text-accent" />
              <span className="text-sm font-medium text-text-primary">Active</span>
            </div>
            <p className="text-2xl font-bold text-accent">
              {loading ? '—' : statistics?.active.formatted || '0'}
            </p>
            <p className="text-xs text-text-tertiary">users online</p>
          </div>

          <div className="bg-surface-2 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-accent" />
              <span className="text-sm font-medium text-text-primary">Recent</span>
            </div>
            <p className="text-2xl font-bold text-accent">
              {loading ? '—' : statistics?.recent.posts || 0}
            </p>
            <p className="text-xs text-text-tertiary">posts today</p>
          </div>
        </div>
      )}

      {/* Category Buttons — hidden pending redesign (see flag at top). */}
      {SHOW_BROWSE_BY_CATEGORY && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-text-secondary mb-3">Browse by Category</h3>
          {categoryConfig.map((category) => {
            const Icon = category.icon;
            const isActive = activeCategory === category.id;

            return (
              <Button
                key={category.id}
                variant={isActive ? "default" : "ghost"}
                onClick={() => handleCategoryClick(category.id)}
                className={cn(
                  "w-full justify-start h-auto p-3",
                  isActive && category.color,
                  !isActive && "hover:bg-surface-3"
                )}
              >
                <div className="flex items-center gap-3 w-full">
                  <Icon className={cn(
                    "h-5 w-5 flex-shrink-0",
                    isActive ? "text-text-on-accent" : "text-text-tertiary"
                  )} />
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{category.label}</span>
                    </div>
                    <p className="text-xs text-text-tertiary mt-0.5">
                      {category.description}
                    </p>
                  </div>
                </div>
              </Button>
            );
          })}
        </div>
      )}

      {/* Poll card slots into the space the category list used to occupy.
          Component returns null when no active poll exists, so the slot
          collapses cleanly without a fallback. */}
      <PollSidebarCard />

      {/* Quick Actions */}
      <div className="pt-4 border-t border-border-subtle">
        <h3 className="text-sm font-semibold text-text-secondary mb-3">Quick Actions</h3>
        <div className="space-y-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full justify-start"
            onClick={() => router.push('/community/create-post')}
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Create New Post
          </Button>
          {/* Browse Workshops — hidden until Phase 2
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start"
            onClick={() => router.push('/workshops')}
          >
            <GraduationCap className="h-4 w-4 mr-2" />
            Browse Workshops
          </Button>
          */}
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full justify-start"
            onClick={() => router.push('/jobs')}
          >
            <Mic className="h-4 w-4 mr-2" />
            Find Auditions
          </Button>
        </div>
      </div>
    </div>
  );
}
